import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Spinner,
} from 'react-bootstrap';
import AdminLayout from '../components/AdminLayout';
import api from '../lib/api';
import { useToast } from '../components/common/ToastProvider';
import { cloneLandingDefaults } from '../components/landing/defaultLandingContent';
import ImageUploadField from '../components/common/ImageUploadField';

const emptyOverviewSection = { title: '', description: '', items: [''] };
const emptyComparison = { label: '', ours: '', others: '' };
const emptyFaq = { q: '', a: '' };

export default function AdminLandingPage() {
  const [settings, setSettings] = useState({
    site_name: '',
    logo_url: '',
    favicon_url: '',
    hero_title: '',
    hero_subtitle: '',
    hero_cta_text: '',
    hero_cta_url: '',
    seo_title: '',
    seo_description: '',
    seo_keywords: '',
    primary_color: '#4f46e5',
    secondary_color: '#0ea5e9',
    support_email: '',
    contact_email: '',
    contact_phone: '',
    footer_text: '',
  });

  const [landingContent, setLandingContent] = useState(cloneLandingDefaults());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { showToast } = useToast();

  const parseLanding = useMemo(
    () => (value) => {
      const defaults = cloneLandingDefaults();
      if (!value) return defaults;

      const parsed =
        typeof value === 'string'
          ? (() => {
              try {
                return JSON.parse(value);
              } catch {
                return null;
              }
            })()
          : value;

      if (!parsed || typeof parsed !== 'object') return defaults;

      return {
        ...defaults,
        ...parsed,
        hero_points: Array.isArray(parsed.hero_points)
          ? parsed.hero_points
          : defaults.hero_points,
        overview_sections: Array.isArray(parsed.overview_sections)
          ? parsed.overview_sections
          : defaults.overview_sections,
        comparison: Array.isArray(parsed.comparison)
          ? parsed.comparison
          : defaults.comparison,
        faq: Array.isArray(parsed.faq) ? parsed.faq : defaults.faq,
        final_cta: parsed.final_cta || defaults.final_cta,
      };
    },
    []
  );

  const load = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get('/admin/settings/site-content/');
      setSettings((prev) => ({ ...prev, ...data }));
      setLandingContent(parseLanding(data?.landing_content));
    } catch {
      showToast('Failed to load landing settings.', 'danger');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setIsSaving(true);
    try {
      await api.post('/admin/settings/site-content/', {
        ...settings,
        landing_content: landingContent,
      });
      showToast('Landing page settings saved.', 'success');
    } catch {
      showToast('Failed to save landing settings.', 'danger');
    } finally {
      setIsSaving(false);
    }
  };

  const setArrayItem = (key, index, value) => {
    const next = [...(landingContent[key] || [])];
    next[index] = value;
    setLandingContent({ ...landingContent, [key]: next });
  };

  const removeArrayItem = (key, index) => {
    const next = [...(landingContent[key] || [])];
    next.splice(index, 1);
    setLandingContent({ ...landingContent, [key]: next });
  };

  const setOverviewSection = (index, patch) => {
    const next = [...(landingContent.overview_sections || [])];
    next[index] = { ...(next[index] || emptyOverviewSection), ...patch };
    setLandingContent({ ...landingContent, overview_sections: next });
  };

  const removeOverviewSection = (index) => {
    const next = [...(landingContent.overview_sections || [])];
    next.splice(index, 1);
    setLandingContent({ ...landingContent, overview_sections: next });
  };

  const setOverviewItem = (sectionIndex, itemIndex, value) => {
    const section =
      landingContent.overview_sections?.[sectionIndex] || emptyOverviewSection;
    const items = Array.isArray(section.items) ? [...section.items] : [];
    items[itemIndex] = value;
    setOverviewSection(sectionIndex, { items });
  };

  const addOverviewItem = (sectionIndex) => {
    const section =
      landingContent.overview_sections?.[sectionIndex] || emptyOverviewSection;
    setOverviewSection(sectionIndex, {
      items: [...(section?.items || []), ''],
    });
  };

  const removeOverviewItem = (sectionIndex, itemIndex) => {
    const section =
      landingContent.overview_sections?.[sectionIndex] || emptyOverviewSection;
    const items = [...(section?.items || [])];
    items.splice(itemIndex, 1);
    setOverviewSection(sectionIndex, { items: items.length ? items : [''] });
  };

  const updateComparisonRow = (index, patch) => {
    const next = [...(landingContent.comparison || [])];
    next[index] = { ...(next[index] || emptyComparison), ...patch };
    setLandingContent({ ...landingContent, comparison: next });
  };

  const removeComparisonRow = (index) => {
    const next = [...(landingContent.comparison || [])];
    next.splice(index, 1);
    setLandingContent({ ...landingContent, comparison: next });
  };

  const updateFaqRow = (index, patch) => {
    const next = [...(landingContent.faq || [])];
    next[index] = { ...(next[index] || emptyFaq), ...patch };
    setLandingContent({ ...landingContent, faq: next });
  };

  const removeFaqRow = (index) => {
    const next = [...(landingContent.faq || [])];
    next.splice(index, 1);
    setLandingContent({ ...landingContent, faq: next });
  };

  const totalOverviewSections = landingContent?.overview_sections?.length || 0;
  const totalHeroPoints = landingContent?.hero_points?.length || 0;
  const totalComparisonRows = landingContent?.comparison?.length || 0;
  const totalFaqRows = landingContent?.faq?.length || 0;

  return (
    <AdminLayout
      title="Landing Page Builder"
      subtitle="Manage your SaaS landing page content, structure, SEO, CTA blocks, and support details."
    >
      <Row className="g-4 mb-4">
        <Col xl={3} md={6}>
          <Card className="admin-surface-card h-100">
            <Card.Body>
              <div className="small text-muted mb-2">Overview Sections</div>
              <div className="h4 fw-bold mb-0">{totalOverviewSections}</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={3} md={6}>
          <Card className="admin-surface-card h-100">
            <Card.Body>
              <div className="small text-muted mb-2">Hero Points</div>
              <div className="h4 fw-bold mb-0">{totalHeroPoints}</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={3} md={6}>
          <Card className="admin-surface-card h-100">
            <Card.Body>
              <div className="small text-muted mb-2">Comparison Rows</div>
              <div className="h4 fw-bold mb-0">{totalComparisonRows}</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={3} md={6}>
          <Card className="admin-surface-card h-100">
            <Card.Body>
              <div className="small text-muted mb-2">FAQ Entries</div>
              <div className="h4 fw-bold mb-0">{totalFaqRows}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="admin-surface-card mb-4">
        <Card.Body className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
          <div>
            <div className="admin-section-title mb-1">Landing Page Configuration</div>
            <div className="text-muted small">
              Update the public-facing landing page content and keep all major sections in sync.
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <Badge bg="light" text="dark" className="border">
              Brand
            </Badge>
            <Badge bg="light" text="dark" className="border">
              Hero
            </Badge>
            <Badge bg="light" text="dark" className="border">
              Overview
            </Badge>
            <Badge bg="light" text="dark" className="border">
              Comparison
            </Badge>
            <Badge bg="light" text="dark" className="border">
              Pricing
            </Badge>
            <Badge bg="light" text="dark" className="border">
              FAQ
            </Badge>
            <Badge bg="light" text="dark" className="border">
              CTA
            </Badge>
          </div>
        </Card.Body>
      </Card>

      {isLoading ? (
        <Card className="admin-surface-card mb-4">
          <Card.Body className="py-5 text-center">
            <Spinner animation="border" />
            <div className="text-muted mt-3">Loading landing page settings...</div>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Card className="admin-surface-card mb-4">
            <Card.Body>
              <div className="admin-section-title">Brand, SEO and Theme</div>
              <Row className="g-3">
                <Col xl={4} md={6}>
                  <Form.Label>Site Name</Form.Label>
                  <Form.Control
                    placeholder="Site name"
                    value={settings.site_name}
                    onChange={(e) =>
                      setSettings({ ...settings, site_name: e.target.value })
                    }
                  />
                </Col>

                <Col xl={4} md={6}>
                  <ImageUploadField
                    label="Logo URL"
                    value={settings.logo_url}
                    placeholder="Logo URL"
                    kind="logo"
                    onChange={(logo_url) =>
                      setSettings({ ...settings, logo_url })
                    }
                  />
                </Col>

                <Col xl={4} md={6}>
                  <ImageUploadField
                    label="Favicon URL"
                    value={settings.favicon_url}
                    placeholder="Favicon URL"
                    kind="favicon"
                    onChange={(favicon_url) =>
                      setSettings({ ...settings, favicon_url })
                    }
                  />
                </Col>

                <Col xl={6}>
                  <Form.Label>SEO Title</Form.Label>
                  <Form.Control
                    placeholder="SEO title"
                    value={settings.seo_title}
                    onChange={(e) =>
                      setSettings({ ...settings, seo_title: e.target.value })
                    }
                  />
                </Col>

                <Col xl={6}>
                  <Form.Label>SEO Keywords</Form.Label>
                  <Form.Control
                    placeholder="SEO keywords"
                    value={settings.seo_keywords}
                    onChange={(e) =>
                      setSettings({ ...settings, seo_keywords: e.target.value })
                    }
                  />
                </Col>

                <Col xs={12}>
                  <Form.Label>SEO Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="SEO description"
                    value={settings.seo_description}
                    onChange={(e) =>
                      setSettings({ ...settings, seo_description: e.target.value })
                    }
                  />
                </Col>

                <Col md={6}>
                  <Form.Label>Primary Color</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="color"
                      value={settings.primary_color || '#4f46e5'}
                      onChange={(e) =>
                        setSettings({ ...settings, primary_color: e.target.value })
                      }
                      style={{ maxWidth: 72 }}
                    />
                    <Form.Control
                      value={settings.primary_color || '#4f46e5'}
                      onChange={(e) =>
                        setSettings({ ...settings, primary_color: e.target.value })
                      }
                    />
                  </InputGroup>
                </Col>

                <Col md={6}>
                  <Form.Label>Secondary Color</Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="color"
                      value={settings.secondary_color || '#0ea5e9'}
                      onChange={(e) =>
                        setSettings({ ...settings, secondary_color: e.target.value })
                      }
                      style={{ maxWidth: 72 }}
                    />
                    <Form.Control
                      value={settings.secondary_color || '#0ea5e9'}
                      onChange={(e) =>
                        setSettings({ ...settings, secondary_color: e.target.value })
                      }
                    />
                  </InputGroup>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="admin-surface-card mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div>
                  <div className="admin-section-title mb-1">Hero Section</div>
                  <div className="text-muted small">
                    Control the first impression users see on the landing page.
                  </div>
                </div>

                <Button
                  size="sm"
                  className="admin-action-btn"
                  onClick={() =>
                    setLandingContent({
                      ...landingContent,
                      hero_points: [...(landingContent.hero_points || []), ''],
                    })
                  }
                >
                  Add Hero Point
                </Button>
              </div>

              <Row className="g-3">
                <Col xl={6}>
                  <Form.Label>Hero Title</Form.Label>
                  <Form.Control
                    placeholder="Hero title"
                    value={settings.hero_title}
                    onChange={(e) =>
                      setSettings({ ...settings, hero_title: e.target.value })
                    }
                  />
                </Col>

                <Col xl={6}>
                  <Form.Label>Hero CTA Text</Form.Label>
                  <Form.Control
                    placeholder="Hero CTA text"
                    value={settings.hero_cta_text}
                    onChange={(e) =>
                      setSettings({ ...settings, hero_cta_text: e.target.value })
                    }
                  />
                </Col>

                <Col xs={12}>
                  <Form.Label>Hero Subtitle</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Hero subtitle"
                    value={settings.hero_subtitle}
                    onChange={(e) =>
                      setSettings({ ...settings, hero_subtitle: e.target.value })
                    }
                  />
                </Col>

                <Col xs={12}>
                  <Form.Label>Hero CTA URL</Form.Label>
                  <Form.Control
                    placeholder="Hero CTA URL"
                    value={settings.hero_cta_url}
                    onChange={(e) =>
                      setSettings({ ...settings, hero_cta_url: e.target.value })
                    }
                  />
                </Col>
              </Row>

              <hr />

              <div className="fw-semibold mb-3">Hero Points</div>
              <div className="d-grid gap-2">
                {(landingContent.hero_points || []).map((point, index) => (
                  <div
                    key={`hero-point-${index}`}
                    className="d-flex gap-2 align-items-center"
                  >
                    <Form.Control
                      value={point}
                      placeholder={`Hero point ${index + 1}`}
                      onChange={(e) =>
                        setArrayItem('hero_points', index, e.target.value)
                      }
                    />
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeArrayItem('hero_points', index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>

          <Card className="admin-surface-card mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div>
                  <div className="admin-section-title mb-1">Overview Sections</div>
                  <div className="text-muted small">
                    Build the core benefit blocks shown on the landing page.
                  </div>
                </div>

                <Button
                  size="sm"
                  className="admin-action-btn"
                  onClick={() =>
                    setLandingContent({
                      ...landingContent,
                      overview_sections: [
                        ...(landingContent.overview_sections || []),
                        { ...emptyOverviewSection },
                      ],
                    })
                  }
                >
                  Add Section
                </Button>
              </div>

              {(landingContent.overview_sections || []).map((section, sectionIndex) => (
                <Card className="mb-3 border-0 shadow-sm" key={`overview-${sectionIndex}`}>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                      <div className="fw-semibold">
                        Overview Section {sectionIndex + 1}
                      </div>
                      <div className="d-flex gap-2">
                        <Badge bg="light" text="dark" className="border">
                          {(section?.items || []).length} items
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => removeOverviewSection(sectionIndex)}
                        >
                          Remove Section
                        </Button>
                      </div>
                    </div>

                    <Row className="g-3 mb-3">
                      <Col lg={4}>
                        <Form.Label>Section Title</Form.Label>
                        <Form.Control
                          value={section?.title || ''}
                          placeholder="Section title"
                          onChange={(e) =>
                            setOverviewSection(sectionIndex, {
                              title: e.target.value,
                            })
                          }
                        />
                      </Col>

                      <Col lg={8}>
                        <Form.Label>Section Description</Form.Label>
                        <Form.Control
                          value={section?.description || ''}
                          placeholder="Section description"
                          onChange={(e) =>
                            setOverviewSection(sectionIndex, {
                              description: e.target.value,
                            })
                          }
                        />
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                      <Form.Label className="mb-0">Section Items</Form.Label>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => addOverviewItem(sectionIndex)}
                      >
                        Add Item
                      </Button>
                    </div>

                    <div className="d-grid gap-2">
                      {(section?.items || []).map((item, itemIndex) => (
                        <div
                          key={`overview-${sectionIndex}-item-${itemIndex}`}
                          className="d-flex gap-2 align-items-center"
                        >
                          <Form.Control
                            value={item}
                            placeholder={`Item ${itemIndex + 1}`}
                            onChange={(e) =>
                              setOverviewItem(sectionIndex, itemIndex, e.target.value)
                            }
                          />
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() =>
                              removeOverviewItem(sectionIndex, itemIndex)
                            }
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </Card.Body>
          </Card>

          <Card className="admin-surface-card mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div>
                  <div className="admin-section-title mb-1">Comparison Section</div>
                  <div className="text-muted small">
                    Highlight the difference between your offer and alternatives.
                  </div>
                </div>

                <Button
                  size="sm"
                  className="admin-action-btn"
                  onClick={() =>
                    setLandingContent({
                      ...landingContent,
                      comparison: [...(landingContent.comparison || []), { ...emptyComparison }],
                    })
                  }
                >
                  Add Row
                </Button>
              </div>

              <Row className="g-3 mb-4">
                <Col md={6}>
                  <Form.Label>Our Label</Form.Label>
                  <Form.Control
                    value={landingContent.comparison_ours_label || ''}
                    placeholder="Us label"
                    onChange={(e) =>
                      setLandingContent({
                        ...landingContent,
                        comparison_ours_label: e.target.value,
                      })
                    }
                  />
                </Col>

                <Col md={6}>
                  <Form.Label>Others Label</Form.Label>
                  <Form.Control
                    value={landingContent.comparison_others_label || ''}
                    placeholder="Others label"
                    onChange={(e) =>
                      setLandingContent({
                        ...landingContent,
                        comparison_others_label: e.target.value,
                      })
                    }
                  />
                </Col>
              </Row>

              {(landingContent.comparison || []).map((row, index) => (
                <Card className="mb-3 border-0 shadow-sm" key={`cmp-${index}`}>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                      <div className="fw-semibold">Comparison Row {index + 1}</div>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => removeComparisonRow(index)}
                      >
                        Remove Row
                      </Button>
                    </div>

                    <Row className="g-3">
                      <Col lg={4}>
                        <Form.Label>Label</Form.Label>
                        <Form.Control
                          value={row.label || ''}
                          placeholder="Label"
                          onChange={(e) =>
                            updateComparisonRow(index, { label: e.target.value })
                          }
                        />
                      </Col>

                      <Col lg={4}>
                        <Form.Label>Our Value</Form.Label>
                        <Form.Control
                          value={row.ours || ''}
                          placeholder="Us"
                          onChange={(e) =>
                            updateComparisonRow(index, { ours: e.target.value })
                          }
                        />
                      </Col>

                      <Col lg={4}>
                        <Form.Label>Other Value</Form.Label>
                        <Form.Control
                          value={row.others || ''}
                          placeholder="Others"
                          onChange={(e) =>
                            updateComparisonRow(index, { others: e.target.value })
                          }
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </Card.Body>
          </Card>

          <Card className="admin-surface-card mb-4">
            <Card.Body>
              <div className="admin-section-title">Pricing Copy Settings</div>
              <Row className="g-3">
                <Col xl={4} md={6}>
                  <Form.Label>Popular Badge</Form.Label>
                  <Form.Control
                    value={landingContent.pricing_popular_label || ''}
                    placeholder="Popular badge"
                    onChange={(e) =>
                      setLandingContent({
                        ...landingContent,
                        pricing_popular_label: e.target.value,
                      })
                    }
                  />
                </Col>

                <Col xl={4} md={6}>
                  <Form.Label>Monthly Suffix</Form.Label>
                  <Form.Control
                    value={landingContent.pricing_monthly_suffix || ''}
                    placeholder="Monthly suffix"
                    onChange={(e) =>
                      setLandingContent({
                        ...landingContent,
                        pricing_monthly_suffix: e.target.value,
                      })
                    }
                  />
                </Col>

                <Col xl={4} md={12}>
                  <Form.Label>Yearly Prefix</Form.Label>
                  <Form.Control
                    value={landingContent.pricing_yearly_prefix || ''}
                    placeholder="Yearly prefix"
                    onChange={(e) =>
                      setLandingContent({
                        ...landingContent,
                        pricing_yearly_prefix: e.target.value,
                      })
                    }
                  />
                </Col>

                <Col md={6}>
                  <Form.Label>Pricing Button Text</Form.Label>
                  <Form.Control
                    value={landingContent.pricing_button_text || ''}
                    placeholder="Pricing button text"
                    onChange={(e) =>
                      setLandingContent({
                        ...landingContent,
                        pricing_button_text: e.target.value,
                      })
                    }
                  />
                </Col>

                <Col md={6}>
                  <Form.Label>Pricing Button URL</Form.Label>
                  <Form.Control
                    value={landingContent.pricing_button_url || ''}
                    placeholder="Pricing button URL"
                    onChange={(e) =>
                      setLandingContent({
                        ...landingContent,
                        pricing_button_url: e.target.value,
                      })
                    }
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="admin-surface-card mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div>
                  <div className="admin-section-title mb-1">FAQ Section</div>
                  <div className="text-muted small">
                    Add the most common questions and answers for visitors.
                  </div>
                </div>

                <Button
                  size="sm"
                  className="admin-action-btn"
                  onClick={() =>
                    setLandingContent({
                      ...landingContent,
                      faq: [...(landingContent.faq || []), { ...emptyFaq }],
                    })
                  }
                >
                  Add Question
                </Button>
              </div>

              {(landingContent.faq || []).map((row, index) => (
                <Card className="mb-3 border-0 shadow-sm" key={`faq-${index}`}>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                      <div className="fw-semibold">FAQ Item {index + 1}</div>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => removeFaqRow(index)}
                      >
                        Remove
                      </Button>
                    </div>

                    <Row className="g-3">
                      <Col lg={5}>
                        <Form.Label>Question</Form.Label>
                        <Form.Control
                          value={row.q || ''}
                          placeholder="Question"
                          onChange={(e) =>
                            updateFaqRow(index, { q: e.target.value })
                          }
                        />
                      </Col>

                      <Col lg={7}>
                        <Form.Label>Answer</Form.Label>
                        <Form.Control
                          value={row.a || ''}
                          placeholder="Answer"
                          onChange={(e) =>
                            updateFaqRow(index, { a: e.target.value })
                          }
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </Card.Body>
          </Card>

          <Card className="admin-surface-card mb-4">
            <Card.Body>
              <div className="admin-section-title">Final CTA Section</div>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>CTA Title</Form.Label>
                  <Form.Control
                    placeholder="Final CTA title"
                    value={landingContent?.final_cta?.title || ''}
                    onChange={(e) =>
                      setLandingContent({
                        ...landingContent,
                        final_cta: {
                          ...(landingContent.final_cta || {}),
                          title: e.target.value,
                        },
                      })
                    }
                  />
                </Col>

                <Col md={6}>
                  <Form.Label>CTA Button Text</Form.Label>
                  <Form.Control
                    placeholder="Final CTA button text"
                    value={landingContent?.final_cta?.button_text || ''}
                    onChange={(e) =>
                      setLandingContent({
                        ...landingContent,
                        final_cta: {
                          ...(landingContent.final_cta || {}),
                          button_text: e.target.value,
                        },
                      })
                    }
                  />
                </Col>

                <Col md={8}>
                  <Form.Label>CTA Subtitle</Form.Label>
                  <Form.Control
                    placeholder="Final CTA subtitle"
                    value={landingContent?.final_cta?.subtitle || ''}
                    onChange={(e) =>
                      setLandingContent({
                        ...landingContent,
                        final_cta: {
                          ...(landingContent.final_cta || {}),
                          subtitle: e.target.value,
                        },
                      })
                    }
                  />
                </Col>

                <Col md={4}>
                  <Form.Label>CTA Button URL</Form.Label>
                  <Form.Control
                    placeholder="Final CTA button URL"
                    value={landingContent?.final_cta?.button_url || ''}
                    onChange={(e) =>
                      setLandingContent({
                        ...landingContent,
                        final_cta: {
                          ...(landingContent.final_cta || {}),
                          button_url: e.target.value,
                        },
                      })
                    }
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="admin-surface-card mb-4">
            <Card.Body>
              <div className="admin-section-title">Footer and Contact Details</div>
              <Row className="g-3">
                <Col lg={6} md={12}>
                  <Form.Label>Footer Text</Form.Label>
                  <Form.Control
                    placeholder="Footer text"
                    value={settings.footer_text}
                    onChange={(e) =>
                      setSettings({ ...settings, footer_text: e.target.value })
                    }
                  />
                </Col>

                <Col lg={6} md={12}>
                  <Form.Label>Support Email</Form.Label>
                  <Form.Control
                    placeholder="Support email"
                    value={settings.support_email}
                    onChange={(e) =>
                      setSettings({ ...settings, support_email: e.target.value })
                    }
                  />
                </Col>

                <Col md={6}>
                  <Form.Label>Contact Email</Form.Label>
                  <Form.Control
                    placeholder="Contact email"
                    value={settings.contact_email}
                    onChange={(e) =>
                      setSettings({ ...settings, contact_email: e.target.value })
                    }
                  />
                </Col>

                <Col md={6}>
                  <Form.Label>Contact Phone</Form.Label>
                  <Form.Control
                    placeholder="Contact phone"
                    value={settings.contact_phone}
                    onChange={(e) =>
                      setSettings({ ...settings, contact_phone: e.target.value })
                    }
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="admin-surface-card mb-4">
            <Card.Body className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
              <div>
                <div className="fw-bold">Ready to publish your changes?</div>
                <div className="text-muted small">
                  Save the landing page content to sync it with the public site.
                </div>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <Button variant="outline-secondary" onClick={load} disabled={isSaving}>
                  Reload
                </Button>
                <Button className="admin-action-btn" onClick={save} disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    'Save Landing Page'
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </>
      )}
    </AdminLayout>
  );
}
